import { Injectable, effect, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LiveService } from '../live.service';
import { displayEventSummary } from '../features/display-sanitizers';

export { isDegenerateAgenticSummary } from '../features/display-sanitizers';

export interface BankEvent {
  id: string;
  type: string;
  summary: string;
  at: string;
}

export function normalizeBankEvent(event: BankEvent): BankEvent {
  return { ...event, summary: displayBankEventSummary(event) };
}

export function displayBankEventSummary(event: BankEvent): string {
  return displayEventSummary(event.type, event.summary);
}

/**
 * The activity feed (the right-bottom panel). Prefers the LIVE SSE stream — events appear the instant they
 * post — and keeps a one-shot poll for history on load plus a slow backup poll as a safety net if SSE drops
 * or the notifications service restarts.
 */
@Injectable({ providedIn: 'root' })
export class BankFeedService {
  private readonly http = inject(HttpClient);
  private readonly live = inject(LiveService);
  readonly events = signal<BankEvent[]>([]);
  private timer: ReturnType<typeof setInterval> | undefined;

  constructor() {
    // append each live event the moment it streams in, de-duped by id
    effect(() => {
      const ev = this.live.last();
      if (ev) {
        const normalized = normalizeBankEvent(ev);
        this.events.update((list) =>
          list.some((x) => x.id === normalized.id) ? list : [normalized, ...list].slice(0, 30),
        );
      }
    });
  }

  start(): void {
    if (this.timer) {
      return;
    }
    this.poll(); // history on load
    // the live SSE connection is opened + org-scoped by AppStore; this service just consumes live.last()
    this.timer = setInterval(() => this.poll(), 15000); // slow safety net (was 4s; SSE now carries the load)
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
  }

  private poll(): void {
    this.http.get<BankEvent[]>('/api/notifications?limit=30').subscribe({
      next: (e) => this.events.set(e.map(normalizeBankEvent)),
      error: () => {
        /* notifications not up / not reachable — leave the last view (live may still be flowing) */
      },
    });
  }
}
